/**
 * The roll call: the whole room, nearest first.
 *
 * The arrow answers "which way to one person". This answers "where is everybody", which is a
 * different question and deserves a different surface — a list you pull up, not something
 * competing with the arrow for the screen.
 *
 * Tapping a row promotes that person to the hero arrow, so the list is also the steering wheel.
 */

import {
  compassPoint,
  formatDistance,
  type ParticipantId,
  type RallyPoint,
  type Role,
} from '@beeline/shared';

import { el } from '../lib/dom';
import { describeClimb, type Geometry, type MemberGeometry } from '../lib/geometry';
import type { MemberState, Staleness } from '../lib/session-model';

export interface RollCallOptions {
  onFocus: (id: ParticipantId | 'rally') => void;
}

/** The room's shared destination, shown at the top because it outranks any one person. */
export interface RallyRow {
  rally: RallyPoint | null;
  rallyGeometry: Geometry;
}

export interface RollCall {
  element: HTMLElement;
  /** The toggle that opens it, kept separate so the caller can put it wherever it belongs. */
  toggle: HTMLElement;
  update: (
    rows: MemberGeometry[],
    focused: ParticipantId | 'rally' | null,
    extra?: RallyRow,
  ) => void;
  destroy: () => void;
}

const ROLE_LABEL: Record<Role, string> = {
  anchor: 'staying put',
  walker: 'walking',
  leader: 'leading',
};

/** The one line under a name. Honesty first: a stale signal outranks anything else worth saying. */
function statusFor(member: MemberState, geometry: MemberGeometry['geometry']): string {
  if (member.converged) return 'arrived';
  if (!member.present && !member.frame) return 'not here yet';

  const stale: Partial<Record<Staleness, string>> = {
    stale: 'signal a few seconds old',
    lost: 'signal lost',
  };
  const warning = stale[member.staleness];
  if (warning) return warning;

  const parts: string[] = [];
  if (geometry.bearing !== null) parts.push(compassPoint(geometry.bearing));
  if (geometry.climbMetres !== null) parts.push(describeClimb(geometry.climbMetres));
  if (member.role) parts.push(ROLE_LABEL[member.role]);

  return parts.join(' · ') || 'here';
}

export function createRollCall(options: RollCallOptions): RollCall {
  const list = el('ul', { class: 'roll__list' });
  const summary = el('span', { class: 'roll__summary' });

  const panel = el(
    'div',
    { class: 'roll', hidden: true, role: 'dialog', 'aria-label': 'Everyone in this session' },
    el('h2', { class: 'roll__title', text: 'Everyone' }),
    list,
  );

  const toggle = el(
    'button',
    { class: 'button button--ghost button--small roll__toggle', type: 'button' },
    summary,
  );

  const setOpen = (open: boolean): void => {
    panel.hidden = !open;
    toggle.setAttribute('aria-expanded', String(open));
  };
  setOpen(false);

  toggle.addEventListener('click', () => setOpen(panel.hidden));
  panel.addEventListener('click', (event) => {
    // Tapping the backdrop closes it; taps on a row are handled by the row itself.
    if (event.target === panel) setOpen(false);
  });

  /** Rows are reused so that opening the panel does not rebuild the DOM every second. */
  const rows = new Map<
    ParticipantId | 'rally',
    { root: HTMLElement; name: HTMLElement; status: HTMLElement; distance: HTMLElement }
  >();

  const rowFor = (id: ParticipantId | 'rally'): NonNullable<ReturnType<typeof rows.get>> => {
    const existing = rows.get(id);
    if (existing) return existing;

    const name = el('span', { class: 'roll__name' });
    const status = el('span', { class: 'roll__status' });
    const distance = el('span', { class: 'roll__distance mono' });

    const button = el(
      'button',
      {
        class: 'roll__row',
        type: 'button',
        onclick: () => {
          options.onFocus(id);
          setOpen(false);
        },
      },
      el('span', { class: 'roll__who' }, name, status),
      distance,
    );

    const root = el('li', {}, button);
    const created = { root, name, status, distance };
    rows.set(id, created);
    list.append(root);
    return created;
  };

  return {
    element: panel,
    toggle,

    update: (entries, focused, extra) => {
      const rally = extra?.rally ?? null;
      summary.textContent = rally
        ? rally.label
        : entries.length === 0
          ? 'Nobody else yet'
          : `${entries.length} ${entries.length === 1 ? 'other' : 'others'}`;

      const seen = new Set<ParticipantId | 'rally'>();

      // The shared destination goes first, because it outranks any one person in the room:
      // if everybody is walking to it, it is the answer and they are context.
      if (rally) {
        seen.add('rally');
        const row = rowFor('rally');
        row.name.textContent = rally.label;
        row.status.textContent = 'meeting point — everyone walks here';
        row.distance.textContent =
          extra?.rallyGeometry.distanceMetres == null
            ? '—'
            : formatDistance(extra.rallyGeometry.distanceMetres);
        row.root.classList.toggle('roll__row-item--focused', focused === 'rally');
        row.root.classList.add('roll__row-item--rally');
        row.root.hidden = false;
        list.append(row.root);
      }

      for (const entry of entries) {
        const { member, geometry } = entry;
        seen.add(member.id);

        const row = rowFor(member.id);
        row.name.textContent = member.name ?? `Person ${member.id.toUpperCase()}`;
        row.status.textContent = statusFor(member, geometry);
        row.distance.textContent =
          geometry.distanceMetres === null ? '—' : formatDistance(geometry.distanceMetres);

        row.root.classList.toggle('roll__row-item--focused', member.id === focused);
        row.root.classList.toggle('roll__row-item--arrived', member.converged);
        row.root.hidden = false;
        // Keep the list in the order it was handed to us: nearest first.
        list.append(row.root);
      }

      for (const [id, row] of rows) {
        if (!seen.has(id)) row.root.hidden = true;
      }
    },

    destroy: () => {
      rows.clear();
      panel.remove();
      toggle.remove();
    },
  };
}
